import React from 'react';
import { Box, IconButton, ListItemIcon, Menu, MenuItem, Tooltip, Typography } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import { EllipsisVertical, Pencil, UserRound } from 'lucide-react';
import type { Employee } from '../../../store/slices/employeeSlice';
import { sbg, transition } from '../../../theme/sbgTokens';

/** Marks the part of `text` that matches the search term (color, never weight). */
export const HighlightText: React.FC<{ text: string; highlight: string }> = ({ text, highlight }) => {
  const term = highlight.trim();
  if (!term) return <>{text}</>;
  const regex = new RegExp(`(${term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
  // split() with one capture group puts the matches at the odd indexes.
  const parts = text.split(regex);
  return (
    <>
      {parts.map((part, index) =>
        index % 2 === 1 ? (
          <Box
            component="mark"
            key={index}
            sx={{ bgcolor: sbg.bg.selectedStrong, color: 'inherit', borderRadius: '2px', px: '1px' }}
          >
            {part}
          </Box>
        ) : (
          <React.Fragment key={index}>{part}</React.Fragment>
        ),
      )}
    </>
  );
};

/** The department marker: a small series-colored dot that always travels with its name. */
export const DeptDot: React.FC<{ color: string; size?: number; sx?: SxProps<Theme> }> = ({ color, size = 8, sx }) => (
  <Box
    component="span"
    aria-hidden="true"
    sx={{ width: size, height: size, borderRadius: '50%', bgcolor: color, flex: 'none', display: 'inline-block', ...sx }}
  />
);

/** Dot + department name (and an optional team when it differs). */
export const DepartmentLabel: React.FC<{
  color: string;
  label: string;
  team?: string | null;
  variant?: 'caption' | 'body2' | 'body1';
  sx?: SxProps<Theme>;
}> = ({ color, label, team, variant = 'caption', sx }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0, ...sx }}>
    <DeptDot color={color} />
    <Typography variant={variant} component="span" noWrap sx={{ color: sbg.text.secondary, minWidth: 0 }}>
      {label}
      {team && team !== label ? ` · ${team}` : ''}
    </Typography>
  </Box>
);

/**
 * Row actions (Edit placement, View profile) stay quiet until the row is
 * hovered, has keyboard focus inside it, or is selected. On touch screens they
 * fold into one always-visible ⋯ menu. Spread onto the row's `sx`; the row
 * sets `data-selected` when it is the selected person.
 */
export const revealRowActionsSx = {
  '@media (hover: hover)': {
    '& .org-row-actions': { opacity: 0, transition: transition('opacity') },
    '&:hover .org-row-actions, &:focus-within .org-row-actions, &[data-selected="true"] .org-row-actions': { opacity: 1 },
    '& .org-row-actions__menu': { display: 'none' },
  },
  '@media (hover: none)': {
    '& .org-row-actions__icons': { display: 'none' },
  },
} as const;

/** Edit placement + View profile for one person, as icons (pointer) or a ⋯ menu (touch). */
export const RowActions: React.FC<{
  employee: Employee;
  canEdit: boolean;
  canViewProfile: boolean;
  onEdit: (employee: Employee) => void;
  onViewProfile: (id: string) => void;
}> = ({ employee, canEdit, canViewProfile, onEdit, onViewProfile }) => {
  const [anchor, setAnchor] = React.useState<HTMLElement | null>(null);
  if (!canEdit && !canViewProfile) return null;
  const stop = (event: React.SyntheticEvent) => event.stopPropagation();
  const menuLabel = `More actions for ${employee.name}`;

  return (
    <Box
      className="org-row-actions"
      onClick={stop}
      onKeyDown={stop}
      sx={{ position: 'relative', zIndex: 1, display: 'inline-flex', alignItems: 'center', flex: 'none' }}
    >
      <Box className="org-row-actions__icons" sx={{ display: 'inline-flex', alignItems: 'center' }}>
        {canEdit && (
          <Tooltip title="Edit placement">
            <IconButton size="small" aria-label={`Edit placement for ${employee.name}`} onClick={() => onEdit(employee)}>
              <Pencil size={16} />
            </IconButton>
          </Tooltip>
        )}
        {canViewProfile && (
          <Tooltip title="View profile">
            <IconButton size="small" aria-label={`View ${employee.name}'s profile`} onClick={() => onViewProfile(employee._id)}>
              <UserRound size={16} />
            </IconButton>
          </Tooltip>
        )}
      </Box>
      <Box className="org-row-actions__menu" sx={{ display: 'inline-flex' }}>
        <IconButton
          size="small"
          aria-label={menuLabel}
          aria-haspopup="menu"
          aria-expanded={Boolean(anchor) || undefined}
          onClick={(event) => setAnchor(event.currentTarget)}
        >
          <EllipsisVertical size={16} />
        </IconButton>
        <Menu
          anchorEl={anchor}
          open={Boolean(anchor)}
          onClose={() => setAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          slotProps={{ list: { 'aria-label': menuLabel } }}
        >
          {canEdit && (
            <MenuItem
              onClick={() => {
                setAnchor(null);
                onEdit(employee);
              }}
            >
              <ListItemIcon>
                <Pencil size={16} />
              </ListItemIcon>
              Edit placement
            </MenuItem>
          )}
          {canViewProfile && (
            <MenuItem
              onClick={() => {
                setAnchor(null);
                onViewProfile(employee._id);
              }}
            >
              <ListItemIcon>
                <UserRound size={16} />
              </ListItemIcon>
              View profile
            </MenuItem>
          )}
        </Menu>
      </Box>
    </Box>
  );
};

/** True when a click came from the keyboard (Enter/Space on a button reports `detail` 0). */
export const viaOf = (event: React.MouseEvent) => (event.detail === 0 ? 'keyboard' : 'pointer') as 'keyboard' | 'pointer';
